import { useEffect, useMemo, useRef, useState } from "react";
import { Answer, RaportSection } from "../../../types/Rows";
import { Row } from "./Row";
import { cn } from "../../../lib/utils";

export const ReportSection = ({
  index,
  section,
  items,
  reportId,
  defaultOpen = false,
  isCentralMonitoring = false,
  flatOffset = 0,
  forceOpen = null,
}: {
  index: number;
  section: RaportSection;
  items: Answer[];
  templateId: string;
  reportId: string;
  defaultOpen?: boolean;
  isCentralMonitoring?: boolean;
  flatOffset?: number;
  forceOpen?: boolean | null;
}) => {
  const [itemsWithChildren, setItemsWithChildren] = useState<Answer[]>([]);
  const [isSettingChildren, setIsSettingChildren] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const prevForceOpen = useRef<boolean | null>(null);

  useEffect(() => {
    if (forceOpen !== null && forceOpen !== prevForceOpen.current) {
      setIsOpen(forceOpen);
      prevForceOpen.current = forceOpen;
    }
  }, [forceOpen]);

  useEffect(() => {
    if (items) {
      let questionsCopy = new Map<string, Answer>();
      items.forEach((q) => questionsCopy.set(q.questionId, q));

      let res = items
        ?.map((element) => {
          if (element.parentId && questionsCopy.has(element.parentId)) {
            const val = questionsCopy.get(element.parentId);
            val.children = val.children
              ? [...val.children.filter((x) => x.answerId !== element.answerId), element]
              : [element];
          }
          return element;
        })
        .sort((x) => (x.parentId == null ? -1 : 1));

      questionsCopy = null;
      setItemsWithChildren(res);
      setIsSettingChildren(false);
    }
  }, [items]);

  const rootItems = useMemo(
    () =>
      itemsWithChildren
        .filter((x) => !x.parentId)
        .sort((a, b) => (a.weight < b.weight ? -1 : 1)),
    [itemsWithChildren]
  );

  const answeredCount = rootItems.filter(
    (x) => x.answer && x.answer !== "" && !x.isBlank
  ).length;
  const totalCount = rootItems.filter((x) => !x.isBlank).length;
  const isDone = totalCount > 0 && answeredCount === totalCount;
  const isPartial = answeredCount > 0 && answeredCount < totalCount;
  const followUpCount = rootItems.filter((x) => x.isPresentInFollowUpLetter).length;

  const numBadge = isDone
    ? "bg-[#2BAE72] text-white"
    : isPartial
    ? "bg-[#F4A832] text-[#0F0F0F]"
    : "bg-[var(--sc-text)] text-[var(--sc-surface)]";

  return (
    <div id={`section-${section.id}`} className="bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-[12px] mb-4 overflow-hidden">
      {/* Section header – hidden for CM reports */}
      {!isCentralMonitoring && (
        <div
          className="flex items-center gap-2.5 px-[18px] py-3 bg-[var(--sc-bg)] border-b border-[var(--sc-border)] cursor-pointer hover:bg-[var(--sc-border)] transition-colors"
          onClick={() => setIsOpen((v) => !v)}
        >
          <span
            className={cn(
              "w-[22px] h-[22px] rounded-[5px] flex items-center justify-center text-[11px] font-semibold flex-shrink-0",
              numBadge
            )}
          >
            {isDone ? "✓" : index}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--sc-text)] flex-1 truncate">
            {index}. {section.name}
          </span>
          <span className="text-[10px] text-[var(--sc-muted)] flex-shrink-0">
            {isDone ? "✓ ukończone" : `${answeredCount}/${totalCount}`}
            {followUpCount > 0 && ` · ${followUpCount} follow-up`}
          </span>
          <span
            className={cn(
              "text-[12px] text-[var(--sc-muted)] flex-shrink-0 transition-transform duration-150",
              isOpen && "rotate-90"
            )}
          >
            ›
          </span>
        </div>
      )}

      {/* Section body */}
      {(isCentralMonitoring || isOpen) && !isSettingChildren && (
        <div>
          {rootItems.map((item, i) => (
            <Row
              index={isCentralMonitoring ? String(flatOffset + i + 1) : index + "." + (i + 1)}
              key={item.answerId}
              input={item}
              reportId={reportId}
              depth={0}
            />
          ))}
          {rootItems.length === 0 && (
            <p className="text-[12px] text-[var(--sc-muted)] px-[18px] py-3">
              Brak pytań w tej sekcji.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
